import type { CorridorDefinition } from "@/types/corridor";

import heroVideo from "@/assets/plain.mp4";

const CITY_A = "Hyderabad";
const CITY_B = "Guntur";

const stats = {
  distanceKm: 285,
  driveTime: "5–6 hrs",
  highway: "NH65",
  zonesA: ["Hitech City", "Gachibowli", "LB Nagar", "Uppal", "Miyapur"],
  zonesB: ["Nallapadu", "Brodipet", "Arundelpet", "Lalpuram", "Kaza Toll"],
  busFare: 700,
  cabFare: 4200,
  carpoolFare: 479,
  monthlySavings: 3200,
  ridesToday: 14,
  completedRides: 980,
  avgRating: 4.7,
  travellers: 2140,
};

const faqs: CorridorDefinition["faqs"] = [
  {
    q: `Is carpool safe between ${CITY_A} and ${CITY_B}?`,
    a: "Yes. Duri verifies profiles, shows ratings and trip details, and supports in-app communication for safer travel decisions.",
  },
  {
    q: `How much can I save on ${CITY_A} to ${CITY_B} rides?`,
    a: `Typical shared seats start around ₹${stats.carpoolFare}, usually far lower than private cabs on this route.`,
  },
  {
    q: `Can I find return rides from ${CITY_B} to ${CITY_A}?`,
    a: `Yes, both directions are supported. You can switch direction and check available seats for return trips.`,
  },
  {
    q: `How long does ${CITY_A}–${CITY_B} take by road?`,
    a: `Usually ${stats.driveTime} via ${stats.highway}, depending on traffic and stopovers.`,
  },
];

export const hyderabadGunturCorridor: CorridorDefinition = {
  slug: "hyderabad-guntur",
  cityA: CITY_A,
  cityB: CITY_B,
  stats,
  heroImageSrc: "/Chalo-uploads/ed3e4cb1-8d78-4271-9f34-a0f4c209dca5.png",
  heroImageAlt: "Intercity road corridor between Hyderabad and Guntur",
  heroVideoSrc: heroVideo,
  seoOgImage:
    "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/64ab5ad3-4f6e-457e-94c1-24567a339f8d/id-preview-1268a46e--a6ccb7a0-9a40-44ba-99d3-be21b3278db5.lovable.app-1774604645516.png",
  midCta: {
    title: "Don't drive alone to Guntur",
    subtitle: "Share seats, split fuel and toll costs, and travel with verified co-travellers.",
  },
  localLife: {
    eyebrow: "Along the corridor",
    title: "Local life & practical stopovers",
    intro: `From city departures in ${CITY_A} to food and family stops near ${CITY_B}, this corridor is ideal for planned intercity sharing.`,
  },
  problemSolution: {
    headline: `Tired of expensive solo travel on ${CITY_A}–${CITY_B}?`,
    sub: "Duri helps you reduce cost, avoid empty seats, and travel with verified profiles.",
  },
  spots: [
    {
      icon: "landmark",
      title: "Charminar stretch",
      meta: "Hyderabad",
      tag: "City start",
      text: "Popular starting zone for early morning intercity departures.",
      featured: true,
    },
    {
      icon: "utensils",
      title: "Suryapet food break",
      meta: "NH65",
      tag: "Meal stop",
      text: "Common break point for tea, breakfast, and quick highway refresh.",
    },
    {
      icon: "building2",
      title: "Arundelpet arrival",
      meta: "Guntur",
      tag: "City center",
      text: "Frequent drop zone with easy local connectivity in Guntur.",
    },
    {
      icon: "treePalm",
      title: "Suburban pickup clusters",
      meta: "Both cities",
      tag: "Convenient pickups",
      text: "Flexible pickup/drop combinations reduce first-mile and last-mile hassle.",
    },
    {
      icon: "mountain",
      title: "Route-friendly drive",
      meta: "Intercity",
      tag: "Smooth travel",
      text: "Steady highway sections make this corridor suitable for recurring shared travel.",
    },
    {
      icon: "waves",
      title: "Weekend family runs",
      meta: "HYD ↔ GNT",
      tag: "High demand",
      text: "Common route for family visits, work trips, and student travel.",
    },
  ],
  happenings: [
    {
      when: "Every weekend",
      title: "High-demand shared rides",
      where: `${CITY_A} ↔ ${CITY_B}`,
      text: "Weekends usually show stronger demand and faster seat fill-up.",
    },
    {
      when: "Mon–Fri mornings",
      title: "Work and study travel",
      where: CITY_A,
      text: "Morning departures are popular for office and academic schedules.",
    },
    {
      when: "Fri evenings",
      title: "Return-to-home traffic",
      where: CITY_B,
      text: "Friday evening and night departures tend to fill quickly.",
    },
  ],
  testimonials: [
    {
      name: "Kiran P.",
      role: "Working professional",
      rating: 5,
      text: "I frequently travel Hyderabad–Guntur and Duri makes it affordable and convenient.",
    },
    {
      name: "Anusha R.",
      role: "Student",
      rating: 5,
      text: "Got reliable rides for weekend travel. Profile verification gives confidence.",
    },
    {
      name: "Ramesh T.",
      role: "Host driver",
      rating: 4,
      text: "I already drove this route often. Sharing seats now offsets my trip cost.",
    },
  ],
  faqs,
};
